import React, { useState, useEffect } from 'react';
import { clientesAPI } from '../api/client';
import { Users, Plus, Search, Edit, Trash2, Mail, Phone } from 'lucide-react';
import ClienteModal from '../components/ClienteModal';

const ClientesPage = () => {
  const [clientes, setClientes] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clienteEditar, setClienteEditar] = useState(null);

  useEffect(() => {
    loadClientes();
  }, []);

  const loadClientes = async () => {
    try {
      const response = await clientesAPI.getAll();
      if (response.data.success) {
        setClientes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      setClientes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    try {
      let response;
      if (clienteEditar) {
        response = await clientesAPI.update(clienteEditar.id, formData);
      } else {
        response = await clientesAPI.create(formData);
      }

      if (response.data.success) {
        alert(clienteEditar ? 'Cliente actualizado' : 'Cliente creado');
        loadClientes();
        setIsModalOpen(false);
        setClienteEditar(null);
      } else {
        alert(response.data.message || 'Error al guardar cliente');
      }
    } catch (error) {
      alert('Error al guardar: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (cliente) => {
    setClienteEditar(cliente);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setClienteEditar(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este cliente?')) return;
    
    try {
      const response = await clientesAPI.delete(id);
      if (response.data.success) {
        alert('Cliente eliminado');
        loadClientes();
      }
    } catch (error) {
      alert('Error al eliminar');
    }
  };

  const clientesFiltrados = clientes.filter(cliente =>
    cliente.nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
    cliente.email?.toLowerCase().includes(busqueda.toLowerCase()) ||
    cliente.telefono?.includes(busqueda)
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Clientes</h1>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Nuevo Cliente
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar clientes..."
              className="input-field pl-10"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teléfono</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">RFC</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    Cargando...
                  </td>
                </tr>
              ) : clientesFiltrados.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    {busqueda ? 'No se encontraron clientes' : 'No hay clientes registrados'}
                  </td>
                </tr>
              ) : (
                clientesFiltrados.map((cliente) => (
                  <tr key={cliente.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <Users className="w-5 h-5 text-gray-400 mr-2" />
                        {cliente.nombre}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {cliente.email && (
                        <div className="flex items-center text-gray-600">
                          <Mail className="w-4 h-4 mr-1" />
                          {cliente.email}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {cliente.telefono && (
                        <div className="flex items-center text-gray-600">
                          <Phone className="w-4 h-4 mr-1" />
                          {cliente.telefono}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">{cliente.rfc || '-'}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button 
                          onClick={() => {
                            setClienteEditar(cliente);
                            setIsModalOpen(true);
                          }}
                          className="text-blue-600 hover:bg-blue-50 p-2 rounded"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDelete(cliente.id)}
                          className="text-red-600 hover:bg-red-50 p-2 rounded"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ClienteModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        cliente={clienteEditar}
      />
    </div>
  );
};

export default ClientesPage;
